import React, { useCallback, useState } from "react";
import { PortalPatient, PortalTherapist } from "../api/contracts";
import { ClientValuesSection } from "../components/ClientValuesSection";
import { CopingSkillsManager } from "../components/CopingSkillsManager";
import { ClientExpectationsSection } from "../components/ClientExpectationsSection";
import { ClientPlanDocumentsSection } from "../components/ClientPlanDocumentsSection";
import { PatientPlanDocumentSummary } from "../api/clientPlanDocumentsApi";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  ClientPrimaryTabs,
  SegmentedNavigation,
} from "../components/PortalNavigation";
import { treatmentNavigation } from "../config/navigation";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";

type TreatmentPageKey =
  | "values"
  | "personalized-coping-skills"
  | "plans-documents"
  | "expectancies";

export function TreatmentPage({
  clinicianCode,
  patientId,
  activeKey,
}: {
  clinicianCode: string;
  patientId: string;
  activeKey: TreatmentPageKey;
}) {
  const workspace = useClientWorkspace();
  const snapshot = getClinicianPortalClientSnapshot(patientId, workspace.accessScope);
  const [therapist, setTherapist] = useState<PortalTherapist | undefined>(
    snapshot?.therapist || undefined,
  );
  const [patient, setPatient] = useState<
    Pick<PortalPatient, "accessCode" | "nickname">
  >(snapshot?.patient);
  const handleContextLoaded = useCallback(
    (loadedTherapist: PortalTherapist, loadedPatient: PortalPatient) => {
      setTherapist(loadedTherapist);
      setPatient(loadedPatient);
    },
    [],
  );
  const handlePlanDocumentsContextLoaded = useCallback(
    (loadedPatient: PatientPlanDocumentSummary) => {
      setPatient({
        accessCode: loadedPatient.accessCode,
        nickname: loadedPatient.nickname,
      });
    },
    [],
  );

  return (
    <ClinicianPortalLayout
      activeGlobalKey={workspace.source === "team" ? "team" : "clients"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        density="compact"
        title={
          patient
            ? `Client Record: ${patient.accessCode} • ${patient.nickname}`
            : "Client Record"
        }
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={clientWorkspaceBreadcrumbs(
              workspace,
              patientId,
              patient?.nickname || "Client",
            )}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="treatment"
        />
      </PortalHeader>
      <div
        className={`cp-page-content cp-page-content--profile cp-page-content--compact-client cp-page-content--sticky-secondary-nav ${
          activeKey === "expectancies"
            ? "cp-page-content--sticky-list"
            : activeKey === "plans-documents"
              ? ""
              : "cp-page-content--sticky-card-list"
        }`}
      >
        <SegmentedNavigation
          items={treatmentNavigation}
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey={activeKey}
        />
        {activeKey === "values" ? (
          <ClientValuesSection
            clinicianCode={clinicianCode}
            patientId={patientId}
            onContextLoaded={handleContextLoaded}
            readOnly={workspace.readOnly}
            accessScope={workspace.accessScope}
          />
        ) : null}
        {activeKey === "personalized-coping-skills" ? (
          <CopingSkillsManager
            clinicianCode={clinicianCode}
            scope={{ kind: "client", patientId }}
            onClientContextLoaded={handleContextLoaded}
            readOnly={workspace.readOnly}
            accessScope={workspace.accessScope}
          />
        ) : null}
        {activeKey === "expectancies" ? (
          <ClientExpectationsSection
            clinicianCode={clinicianCode}
            patientId={patientId}
            onContextLoaded={handleContextLoaded}
            readOnly={workspace.readOnly}
            accessScope={workspace.accessScope}
          />
        ) : null}
        {activeKey === "plans-documents" ? (
          <ClientPlanDocumentsSection
            clinicianCode={clinicianCode}
            patientId={patientId}
            onContextLoaded={handlePlanDocumentsContextLoaded}
            readOnly={workspace.readOnly}
            accessScope={workspace.accessScope}
          />
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
